import React, { useEffect, useMemo, useState } from "react";
import {
  FaClipboardCheck,
  FaCheckCircle,
  FaTimesCircle,
  FaEye,
  FaTimes,
  FaUserTie,
  FaSearch,
  FaFilter,
  FaHistory,
} from "react-icons/fa";
import { toast } from "react-toastify";
import { getData, putData } from "../../../services/FetchNodeservices";

const formatDate = (dateString) => {
  if (!dateString) return "-";
  try {
    return new Date(dateString).toLocaleDateString("en-GB");
  } catch {
    return dateString;
  }
};

const statusBadge = (s) =>
  s === "Approved"
    ? "bg-green-100 text-green-700 border-green-400"
    : s === "Rejected"
    ? "bg-red-100 text-red-700 border-red-400"
    : "bg-yellow-100 text-yellow-700 border-yellow-400";

export default function LeaveApproval() {
  const user = JSON.parse(localStorage.getItem("USER"));

  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(false);

  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("Pending");

  const [openModal, setOpenModal] = useState(false);
  const [selectedReq, setSelectedReq] = useState(null);

  const [empBalance, setEmpBalance] = useState(null);
  const [balLoading, setBalLoading] = useState(false);

  const [empHistory, setEmpHistory] = useState([]);
  const [historyLoading, setHistoryLoading] = useState(false);

  const [tab, setTab] = useState("preview");

  /* ================= ADMIN FETCH ALL REQUESTS ================= */
  const fetchRequests = async () => {
    try {
      setLoading(true);

      const res = await getData("leave/admin/all");
      if (res?.status) setRequests(res.data || []);
      else toast.error(res?.message || "Failed to fetch leave requests");
    } catch (err) {
      console.error(err);
      toast.error("Error fetching leave requests");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchRequests();
    // eslint-disable-next-line
  }, []);

  /* ================= HELPERS ================= */
  const bucketLabel = (b) => (b === "PL" ? "PL (Annual)" : "CL (Casual)");

  const leaveCounters = (leaveType) => {
    if (leaveType === "Short Leave") return { short_leave: 1, half_day: 0, full_day: 0 };
    if (leaveType === "Half Day") return { short_leave: 0, half_day: 1, full_day: 0 };
    if (leaveType === "Full Day") return { short_leave: 0, half_day: 0, full_day: 1 };
    return { short_leave: 0, half_day: 0, full_day: 0 };
  };

  /* ================= FILTERED REQUESTS ================= */
  const filteredRequests = useMemo(() => {
    let data = [...requests];

    if (statusFilter !== "All") {
      data = data.filter((x) => x.status === statusFilter);
    }

    if (search) {
      data = data.filter((x) =>
        (x.user_name || "").toLowerCase().includes(search.toLowerCase())
      );
    }

    return data;
  }, [requests, statusFilter, search]);

  /* ================= FETCH EMP HISTORY ================= */
  const fetchEmployeeHistory = async (userId) => {
    try {
      setHistoryLoading(true);
      const res = await getData(`leave/my-leaves/${userId}`);
      if (res?.status) setEmpHistory(res.data || []);
      else setEmpHistory([]);
    } catch (err) {
      console.error(err);
      setEmpHistory([]);
    } finally {
      setHistoryLoading(false);
    }
  };

  /* ================= OPEN PREVIEW MODAL ================= */
  const handlePreview = async (reqObj) => {
    setSelectedReq(reqObj);
    setOpenModal(true);
    setEmpBalance(null);
    setEmpHistory([]);
    setTab("preview");

    try {
      setBalLoading(true);
      const res = await getData(`leave/balance/${reqObj.user_id}`);
      if (res?.status) setEmpBalance(res.data);
      else toast.error(res?.message || "Failed to fetch employee balance");
    } catch (err) {
      console.error(err);
      toast.error("Balance fetch error");
    } finally {
      setBalLoading(false);
    }

    fetchEmployeeHistory(reqObj.user_id);
  };

  /* ================= ADMIN APPROVE / REJECT ================= */
  const handleStatusUpdate = async (id, status) => {
    try {
      const res = await putData(`leave/admin-update/${id}`, {
        status,
        admin_id: user.id,
      });

      if (res?.status) {
        toast.success(res.message || `Leave ${status} successfully!`);

        // ✅ instant UI update from backend updated row
        if (res?.data) {
          setRequests((prev) => prev.map((r) => (r.id === id ? res.data : r)));
          setSelectedReq(res.data);
        } else {
          fetchRequests();
        }

        setOpenModal(false);
      } else {
        toast.error(res?.message || "Failed to update leave status");
      }
    } catch (err) {
      console.error(err);
      toast.error("Server error while updating");
    }
  };

  /* ================= BALANCE PREVIEW ================= */
  const renderPreview = () => {
    if (!selectedReq || !empBalance) return null;

    const leaveDays = parseFloat(selectedReq.deducted_days || 0);

    const beforeCL = parseFloat(empBalance.cl_sl_remaining || 0);
    const beforePL = parseFloat(empBalance.pl_annual_remaining || 0);
    const beforeTotal = parseFloat(empBalance.total_leaves || 0);

    let afterCL = beforeCL;
    let afterPL = beforePL;

    if (selectedReq.leave_bucket === "CL") afterCL = beforeCL - leaveDays;
    if (selectedReq.leave_bucket === "PL") afterPL = beforePL - leaveDays;

    const afterTotal = beforeTotal - leaveDays;

    const countersBefore = {
      short_leave: parseInt(empBalance.short_leave || 0),
      half_day: parseInt(empBalance.half_day || 0),
      full_day: parseInt(empBalance.full_day || 0),
    };

    const addCounter = leaveCounters(selectedReq.leave_type);

    const countersAfter = {
      short_leave: countersBefore.short_leave + addCounter.short_leave,
      half_day: countersBefore.half_day + addCounter.half_day,
      full_day: countersBefore.full_day + addCounter.full_day,
    };

    const warn = afterCL < 0 || afterPL < 0 || afterTotal < 0;

    return (
      <div className="mt-4 bg-gray-50 border rounded-lg p-4">
        <h3 className="font-semibold text-gray-700 mb-3">
          Balance Preview (Before → After)
        </h3>

        {warn && (
          <div className="mb-3 text-sm p-2 rounded bg-red-100 text-red-700 border border-red-300">
            ⚠️ Warning: Approval may cause negative balance.
          </div>
        )}

        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 text-sm">
          <div className="bg-white p-3 rounded border">
            <p className="text-gray-500">CL Balance</p>
            <p className="font-bold text-gray-800">
              {beforeCL.toFixed(2)} →{" "}
              <span className={afterCL < 0 ? "text-red-600" : "text-green-700"}>
                {afterCL.toFixed(2)}
              </span>
            </p>
          </div>

          <div className="bg-white p-3 rounded border">
            <p className="text-gray-500">PL Balance</p>
            <p className="font-bold text-gray-800">
              {beforePL.toFixed(2)} →{" "}
              <span className={afterPL < 0 ? "text-red-600" : "text-green-700"}>
                {afterPL.toFixed(2)}
              </span>
            </p>
          </div>

          <div className="bg-white p-3 rounded border">
            <p className="text-gray-500">Total Leaves</p>
            <p className="font-bold text-gray-800">
              {beforeTotal.toFixed(2)} →{" "}
              <span className={afterTotal < 0 ? "text-red-600" : "text-green-700"}>
                {afterTotal.toFixed(2)}
              </span>
            </p>
          </div>
        </div>

        <div className="mt-4 grid grid-cols-1 sm:grid-cols-3 gap-3 text-sm">
          <div className="bg-white p-3 rounded border">
            <p className="text-gray-500">Short Leave Counter</p>
            <p className="font-bold">
              {countersBefore.short_leave} →{" "}
              <span className="text-blue-700">{countersAfter.short_leave}</span>
            </p>
          </div>

          <div className="bg-white p-3 rounded border">
            <p className="text-gray-500">Half Day Counter</p>
            <p className="font-bold">
              {countersBefore.half_day} →{" "}
              <span className="text-blue-700">{countersAfter.half_day}</span>
            </p>
          </div>

          <div className="bg-white p-3 rounded border">
            <p className="text-gray-500">Full Day Counter</p>
            <p className="font-bold">
              {countersBefore.full_day} →{" "}
              <span className="text-blue-700">{countersAfter.full_day}</span>
            </p>
          </div>
        </div>
      </div>
    );
  };

  /* ================= UI ================= */
  return (
    <div className="min-h-screen bg-gray-50 pt-20 px-4 md:ml-72 md:px-6">
      <div className="bg-white shadow-xl rounded-xl border border-gray-200 p-4 sm:p-6">
        {/* HEADER */}
        <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between mb-4 border-b pb-3">
          <div className="flex items-center gap-2">
            <FaClipboardCheck size={22} className="text-green-600" />
            <h2 className="text-lg font-bold text-gray-700">
              All Leave Requests (Admin)
            </h2>
          </div>

          {/* FILTERS */}
          <div className="flex flex-col sm:flex-row gap-2 w-full sm:w-auto">
            <div className="relative w-full sm:w-72">
              <FaSearch className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 text-sm" />
              <input
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Search employee..."
                className="w-full pl-9 pr-3 py-2 border rounded-lg text-sm outline-none focus:ring-2 focus:ring-green-200"
              />
            </div>

            <div className="relative w-full sm:w-44">
              <FaFilter className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 text-sm" />
              <select
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value)}
                className="w-full pl-9 pr-3 py-2 border rounded-lg text-sm outline-none"
              >
                <option value="Pending">Pending</option>
                <option value="Approved">Approved</option>
                <option value="Rejected">Rejected</option>
                <option value="All">All</option>
              </select>
            </div>

            <button
              onClick={fetchRequests}
              className="px-4 py-2 rounded-lg text-sm bg-green-600 text-white hover:bg-green-700"
            >
              Refresh
            </button>
          </div>
        </div>

        {/* TABLE */}
        <div className="overflow-x-auto rounded-lg border border-gray-200">
          <table className="min-w-[1500px] w-full text-sm">
            <thead className="bg-gray-100 border-b text-gray-700 text-xs uppercase">
              <tr>
                <th className="p-3 text-left">Employee</th>
                <th className="p-3 text-left">Leave Type</th>
                <th className="p-3 text-left">Bucket</th>
                <th className="p-3 text-center">Deduction</th>
                <th className="p-3 text-left">From</th>
                <th className="p-3 text-left">To</th>
                <th className="p-3 text-left">Reason</th>
                <th className="p-3 text-left">Applied On</th>
                <th className="p-3 text-left">Status</th>
                <th className="p-3 text-left">Approved By</th>
                <th className="p-3 text-left">Action</th>
              </tr>
            </thead>

            <tbody>
              {loading ? (
                <tr>
                  <td colSpan="11" className="p-6 text-center text-gray-500">
                    Loading leave requests...
                  </td>
                </tr>
              ) : filteredRequests.length === 0 ? (
                <tr>
                  <td colSpan="11" className="p-6 text-center text-gray-500">
                    No leave requests found.
                  </td>
                </tr>
              ) : (
                filteredRequests.map((req) => (
                  <tr key={req.id} className="border-b hover:bg-gray-50">
                    <td className="p-3 font-medium">{req.user_name}</td>
                    <td className="p-3">{req.leave_type}</td>
                    <td className="p-3">
                      <span className="px-2 py-1 rounded text-xs font-semibold border bg-blue-50 text-blue-700 border-blue-200">
                        {bucketLabel(req.leave_bucket)}
                      </span>
                    </td>
                    <td className="p-3 text-center font-semibold">{req.deducted_days}</td>
                    <td className="p-3">{formatDate(req.date_from)}</td>
                    <td className="p-3">{formatDate(req.date_to)}</td>
                    <td className="p-3 text-gray-600">{req.reason || "-"}</td>
                    <td className="p-3 text-gray-500">{formatDate(req.created_at)}</td>

                    <td className="p-3">
                      <span className={`px-2 py-1 rounded text-xs font-semibold border ${statusBadge(req.status)}`}>
                        {req.status}
                      </span>
                    </td>

                    <td className="p-3">
                      {req.approved_by_name ? (
                        <span className="inline-flex items-center gap-2">
                          <FaUserTie className="text-indigo-600" />
                          {req.approved_by_name}
                        </span>
                      ) : "-"}
                    </td>

                    <td className="p-3">
                      <div className="flex gap-2">
                        <button
                          onClick={() => handlePreview(req)}
                          className="px-3 py-1 text-xs rounded flex items-center gap-1 bg-gray-800 text-white"
                        >
                          <FaEye /> Preview
                        </button>

                        {/* <button
                          disabled={req.status !== "Pending"}
                          onClick={() => handleStatusUpdate(req.id, "Approved")}
                          className={`px-3 py-1 text-xs rounded flex items-center gap-1 ${
                            req.status !== "Pending"
                              ? "bg-gray-200 text-gray-500 cursor-not-allowed"
                              : "bg-green-600 text-white hover:bg-green-700"
                          }`}
                        >
                          <FaCheckCircle /> Approve
                        </button> */}

                        {/* <button
                          disabled={req.status !== "Pending"}
                          onClick={() => handleStatusUpdate(req.id, "Rejected")}
                          className={`px-3 py-1 text-xs rounded flex items-center gap-1 ${
                            req.status !== "Pending"
                              ? "bg-gray-200 text-gray-500 cursor-not-allowed"
                              : "bg-red-600 text-white hover:bg-red-700"
                          }`}
                        >
                          <FaTimesCircle /> Reject
                        </button> */}
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        <div className="mt-4 text-sm text-gray-500">
          Showing <b>{filteredRequests.length}</b> of <b>{requests.length}</b> requests
        </div>
      </div>

      {/* MODAL */}
      {openModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
          <div className="bg-white w-full max-w-6xl rounded-xl shadow-lg border relative">
            <div className="flex items-center justify-between p-4 border-b">
              <div>
                <h3 className="font-bold text-gray-700 text-lg">
                  Leave Preview - {selectedReq?.user_name}
                </h3>
                <p className="text-xs text-gray-500 mt-1">
                  Status: <span className="font-semibold">{selectedReq?.status}</span>
                </p>
              </div>

              <button
                onClick={() => setOpenModal(false)}
                className="p-2 rounded hover:bg-gray-100"
              >
                <FaTimes />
              </button>
            </div>

            <div className="flex gap-2 px-4 pt-3">
              <button
                onClick={() => setTab("preview")}
                className={`px-4 py-2 rounded-t-lg text-sm ${
                  tab === "preview"
                    ? "bg-white border border-gray-200 font-semibold"
                    : "bg-gray-100 border border-gray-200 text-gray-600"
                }`}
              >
                Balance Preview
              </button>

              <button
                onClick={() => setTab("history")}
                className={`px-4 py-2 rounded-t-lg text-sm flex items-center gap-2 ${
                  tab === "history"
                    ? "bg-white border border-gray-200 font-semibold"
                    : "bg-gray-100 border border-gray-200 text-gray-600"
                }`}
              >
                <FaHistory /> Employee History
              </button>
            </div>

            <div className="p-4 border-t">
              {tab === "preview" ? (
                balLoading ? (
                  <div className="p-6 text-center text-gray-500">
                    Loading balance preview...
                  </div>
                ) : (
                  renderPreview()
                )
              ) : (
                <div className="mt-2">
                  {historyLoading ? (
                    <div className="p-6 text-center text-gray-500">
                      Loading employee leave history...
                    </div>
                  ) : (
                    <div className="overflow-auto rounded-lg border">
                      <table className="min-w-[1000px] w-full text-sm">
                        <thead className="bg-gray-100 border-b text-gray-700 text-xs uppercase">
                          <tr>
                            <th className="p-3 text-left">Type</th>
                            <th className="p-3 text-left">Bucket</th>
                            <th className="p-3 text-center">Days</th>
                            <th className="p-3 text-left">From</th>
                            <th className="p-3 text-left">To</th>
                            <th className="p-3 text-left">Applied</th>
                            <th className="p-3 text-left">Status</th>
                            <th className="p-3 text-left">Approved By</th>
                          </tr>
                        </thead>

                        <tbody>
                          {empHistory.length === 0 ? (
                            <tr>
                              <td colSpan="8" className="p-6 text-center text-gray-500">
                                No leave history found.
                              </td>
                            </tr>
                          ) : (
                            empHistory.map((row) => (
                              <tr key={row.id} className="border-b hover:bg-gray-50">
                                <td className="p-3">{row.leave_type}</td>
                                <td className="p-3">{row.leave_bucket}</td>
                                <td className="p-3 text-center font-bold">{row.deducted_days}</td>
                                <td className="p-3">{formatDate(row.date_from)}</td>
                                <td className="p-3">{formatDate(row.date_to)}</td>
                                <td className="p-3">{formatDate(row.created_at)}</td>
                                <td className="p-3">
                                  <span className={`px-2 py-1 rounded text-xs border font-semibold ${statusBadge(row.status)}`}>
                                    {row.status}
                                  </span>
                                </td>
                                <td className="p-3">{row.approved_by_name || "-"}</td>
                              </tr>
                            ))
                          )}
                        </tbody>
                      </table>
                    </div>
                  )}
                </div>
              )}
            </div>

            <div className="p-4 border-t flex justify-end">
              <button
                onClick={() => setOpenModal(false)}
                className="px-4 py-2 rounded border hover:bg-gray-50"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
